import React, { useState, useRef } from 'react';
import { ConsultantNode, PayoutBreakdown } from '../../types/career';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { formatAE, formatCurrencyUAH, formatPercent } from '../../utils/formatters';
import { getLevelTitle, isDirectorLevel, isCoOwnerLevel } from '../../math/careerRules';
import { calculateCareerGap } from '../../math/qualification';
import { countActiveConsultants, countTotalConsultants } from '../../utils/treeUtils';
import { calculateTreeLayout } from '../canvas/TreeLayout';
import {
  Printer,
  CheckSquare,
  Square,
  FileDown,
  Layers,
  BarChart3,
  FileText,
  Network,
} from 'lucide-react';

export interface DocumentExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  rootNode: ConsultantNode;
  payout: PayoutBreakdown;
  simulationMonth?: number;
}

export const DocumentExportModal: React.FC<DocumentExportModalProps> = ({
  isOpen,
  onClose,
  rootNode,
  payout,
  simulationMonth,
}) => {
  const [includeMap, setIncludeMap] = useState(true);
  const [includeMetrics, setIncludeMetrics] = useState(true);
  const [includeCheck, setIncludeCheck] = useState(true);
  const [documentTitle, setDocumentTitle] = useState(
    `Розрахунковий документ — ${rootNode.name}`
  );

  const printAreaRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const levelTitle = getLevelTitle(rootNode.level);
  const totalConsultants = countTotalConsultants(rootNode);
  const activeConsultants = countActiveConsultants(rootNode);
  const directBranchLevels = rootNode.children.map((c) => c.level);
  const gapAnalysis = calculateCareerGap(
    rootNode.level,
    rootNode.totalCumulativeAE,
    rootNode.personalCumulativeAE,
    directBranchLevels
  );

  const isDirector = isDirectorLevel(payout.level);
  const isCoOwner = isCoOwnerLevel(payout.level);

  const effectiveTaxRate =
    payout.totalGrossIncomeUAH > 0
      ? payout.totalTaxesUAH / payout.totalGrossIncomeUAH
      : 0;

  const handlePrint = () => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'visible';
    document.documentElement.style.overflow = 'visible';

    window.print();

    setTimeout(() => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    }, 500);
  };

  // Helper to render visual SVG map of complete organization structure
  const renderVisualStructureMap = (root: ConsultantNode) => {
    const layout = calculateTreeLayout(root, 'vertical');
    const padX = 70;
    const padY = 45;
    const minX = layout.bounds.minX - padX;
    const minY = layout.bounds.minY - padY;
    const width = Math.max(340, layout.bounds.maxX - layout.bounds.minX + padX * 2);
    const height = Math.max(180, layout.bounds.maxY - layout.bounds.minY + padY * 2);

    const CARD_W = 104;
    const CARD_H = 58;

    const getLevelFill = (level: number) => {
      switch (level) {
        case 1: return { border: '#10b981', badge: '#10b981', bg: '#064e3b' };
        case 2: return { border: '#06b6d4', badge: '#06b6d4', bg: '#083344' };
        case 3: return { border: '#6366f1', badge: '#6366f1', bg: '#1e1b4b' };
        case 4: return { border: '#a855f7', badge: '#a855f7', bg: '#3b0764' };
        case 5: return { border: '#f59e0b', badge: '#f59e0b', bg: '#451a03' };
        case 6: return { border: '#ea580c', badge: '#ea580c', bg: '#431407' };
        case 7: return { border: '#ef4444', badge: '#ef4444', bg: '#450a0a' };
        case 8: return { border: '#3b82f6', badge: '#3b82f6', bg: '#172554' };
        default: return { border: '#64748b', badge: '#64748b', bg: '#1e293b' };
      }
    };

    return (
      <div className="w-full bg-slate-900/70 print:bg-slate-50 border border-white/10 print:border-gray-300 rounded-xl p-3.5 space-y-2 print-avoid-break">
        <div className="flex items-center justify-between text-xs font-semibold text-text-muted print:text-gray-600">
          <span className="uppercase tracking-wider flex items-center gap-1.5 text-accent-neon print:text-emerald-700">
            <Network className="w-3.5 h-3.5" />
            <span>Візуальна мапа організаційної структури</span>
          </span>
          <span className="font-mono text-[11px]">
            Всього на мапі: {layout.nodes.length} чол.
          </span>
        </div>

        <div className="w-full overflow-x-auto print:overflow-visible custom-scrollbar flex justify-center py-2">
          <svg
            viewBox={`${minX} ${minY} ${width} ${height}`}
            className="w-full h-auto max-h-[340px] print:max-h-[340px] min-w-[480px] print:min-w-0"
            style={{ maxWidth: '100%' }}
          >
            {/* Connecting Lines */}
            {layout.edges.map((edge) => {
              const startY = edge.sourceY + CARD_H / 2;
              const endY = edge.targetY - CARD_H / 2;
              const midY = (startY + endY) / 2;
              const pathD = `M ${edge.sourceX} ${startY} C ${edge.sourceX} ${midY}, ${edge.targetX} ${midY}, ${edge.targetX} ${endY}`;

              return (
                <path
                  key={`${edge.sourceId}-${edge.targetId}`}
                  d={pathD}
                  stroke="#38bdf8"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  fill="none"
                  opacity="0.6"
                  className="print:stroke-gray-400"
                />
              );
            })}

            {/* Consultant Nodes */}
            {layout.nodes.map((node) => {
              const x = node.x - CARD_W / 2;
              const y = node.y - CARD_H / 2;
              const colors = getLevelFill(node.data.level);

              return (
                <g key={node.id}>
                  {/* Card Background */}
                  <rect
                    x={x}
                    y={y}
                    width={CARD_W}
                    height={CARD_H}
                    rx={8}
                    fill={colors.bg}
                    stroke={colors.border}
                    strokeWidth="1.5"
                    className="print:fill-white print:stroke-gray-400"
                  />

                  {/* Level Pill */}
                  <rect
                    x={x + 5}
                    y={y + 5}
                    width={22}
                    height={14}
                    rx={4}
                    fill={colors.badge}
                  />
                  <text
                    x={x + 16}
                    y={y + 15.5}
                    fontSize="9"
                    fontWeight="bold"
                    fill="#020617"
                    textAnchor="middle"
                    fontFamily="monospace"
                  >
                    Р{node.data.level}
                  </text>

                  {/* Name */}
                  <text
                    x={x + 31}
                    y={y + 16}
                    fontSize="9.5"
                    fontWeight="bold"
                    fill="#ffffff"
                    className="print:fill-black"
                  >
                    {node.data.name.length > 9 ? node.data.name.slice(0, 9) + '…' : node.data.name}
                  </text>

                  {/* Line Divider */}
                  <line
                    x1={x + 5}
                    y1={y + 25}
                    x2={x + CARD_W - 5}
                    y2={y + 25}
                    stroke="rgba(255,255,255,0.12)"
                    className="print:stroke-gray-200"
                  />

                  {/* Monthly Personal & Total AE */}
                  <text
                    x={x + 7}
                    y={y + 38}
                    fontSize="8.5"
                    fill="#94a3b8"
                    className="print:fill-gray-600"
                  >
                    Особ: <tspan fontWeight="bold" fill="#34d399" className="print:fill-emerald-800">{formatAE(node.data.personalMonthlyAE, 0)}</tspan>
                  </text>
                  <text
                    x={x + 7}
                    y={y + 50}
                    fontSize="8.5"
                    fill="#94a3b8"
                    className="print:fill-gray-600"
                  >
                    Група: <tspan fontWeight="bold" fill="#38bdf8" className="print:fill-blue-800">{formatAE(node.data.totalMonthlyAE, 0)}</tspan>
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  // Helper to render organizational hierarchy under direct branches
  const renderDownlineBranch = (node: ConsultantNode, depth = 1): React.ReactNode => {
    if (!node.children || node.children.length === 0) return null;
    return (
      <div className="pl-3 sm:pl-4 border-l border-white/10 print:border-gray-300 mt-2 space-y-1.5">
        {node.children.map((child) => (
          <div key={child.id} className="text-[11px] text-text-secondary print:text-gray-700">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge level={child.level} size="sm">Р{child.level}</Badge>
              <span className="font-semibold text-white print:text-black">{child.name}</span>
              <span className="text-text-muted print:text-gray-500 font-mono text-[10px]">
                • Особисті: {formatAE(child.personalMonthlyAE)} • Всього: {formatAE(child.totalMonthlyAE)}
              </span>
            </div>
            {child.children && child.children.length > 0 && depth < 4 && renderDownlineBranch(child, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <Printer className="w-5 h-5 text-accent-emerald" />
          <span>Формування документа структури та доходів</span>
        </div>
      }
      subtitle="Оберіть розділи для включення в документ, збереження в PDF або роздруківки"
    >
      <div className="space-y-6">
        {/* Document Customizer Bar */}
        <div className="glass-panel rounded-2xl p-4 border border-white/10 space-y-3 bg-slate-900/90 print:hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-text-muted uppercase tracking-wider block mb-1">
                Назва документа:
              </label>
              <input
                type="text"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                className="
                  w-full px-3 py-1.5 text-base sm:text-xs rounded-xl bg-slate-950/80 border border-white/15
                  text-white focus:border-accent-emerald focus:outline-none min-h-[36px]
                "
              />
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto pt-1">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<FileDown className="w-4 h-4" />}
                onClick={handlePrint}
              >
                Роздрукувати / Зберегти як PDF
              </Button>
            </div>
          </div>

          {/* Checkboxes for Sections */}
          <div className="pt-2 border-t border-white/10 flex items-center gap-4 sm:gap-6 flex-wrap text-xs">
            <span className="text-text-muted font-medium">Включити в документ:</span>

            <button
              type="button"
              onClick={() => setIncludeMap(!includeMap)}
              className="flex items-center gap-1.5 cursor-pointer text-text-primary hover:text-accent-neon transition-colors select-none"
            >
              {includeMap ? (
                <CheckSquare className="w-4 h-4 text-accent-neon" />
              ) : (
                <Square className="w-4 h-4 text-text-muted" />
              )}
              <span>1. Мапа структури</span>
            </button>

            <button
              type="button"
              onClick={() => setIncludeMetrics(!includeMetrics)}
              className="flex items-center gap-1.5 cursor-pointer text-text-primary hover:text-accent-neon transition-colors select-none"
            >
              {includeMetrics ? (
                <CheckSquare className="w-4 h-4 text-accent-neon" />
              ) : (
                <Square className="w-4 h-4 text-text-muted" />
              )}
              <span>2. Показники та цілі</span>
            </button>

            <button
              type="button"
              onClick={() => setIncludeCheck(!includeCheck)}
              className="flex items-center gap-1.5 cursor-pointer text-text-primary hover:text-accent-neon transition-colors select-none"
            >
              {includeCheck ? (
                <CheckSquare className="w-4 h-4 text-accent-neon" />
              ) : (
                <Square className="w-4 h-4 text-text-muted" />
              )}
              <span>3. Деталізація чеку</span>
            </button>
          </div>
        </div>

        {/* Live Document Preview / Printable Container */}
        <div
          ref={printAreaRef}
          id="starlife-printable-document"
          className="
            bg-slate-950 rounded-2xl p-5 sm:p-8 border border-white/10 space-y-6
            max-h-[60vh] overflow-y-auto custom-scrollbar shadow-2xl
            print:max-h-none print:overflow-visible print:border-none print:p-0 print:bg-white print:text-black
          "
        >
          {/* Document Official Header */}
          <div className="border-b border-white/15 pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-accent-emerald to-accent-cyan flex items-center justify-center text-slate-950 font-black text-xl shadow-lg flex-shrink-0">
                SL
              </div>
              <div>
                <div className="text-[11px] font-bold text-accent-neon uppercase tracking-widest print:text-emerald-700">
                  Starlife Financial Group
                </div>
                <h2 className="text-lg sm:text-xl font-extrabold text-white print:text-black">
                  {documentTitle}
                </h2>
                <p className="text-xs text-text-muted print:text-gray-600 mt-0.5">
                  Офіційний документ розрахунку структури, кар'єрного росту та винагороди
                  {simulationMonth ? ` • Симуляція: ${simulationMonth}-й місяць` : ''}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:items-end text-xs">
              <Badge level={rootNode.level} size="lg">
                Рівень {rootNode.level} • {levelTitle}
              </Badge>
              <div className="text-[11px] text-text-muted print:text-gray-600 mt-1 font-mono">
                Сформовано: {new Date().toLocaleDateString('uk-UA')}
              </div>
            </div>
          </div>

          {/* SECTION 1: Organization Structure Map & Breakdown */}
          {includeMap && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-2 text-sm font-bold text-cyan-400 uppercase tracking-wider print:text-cyan-800 border-b border-white/10 pb-2">
                <Layers className="w-4 h-4 flex-shrink-0" />
                <span>1. Організаційна структура команди ({totalConsultants} чол.)</span>
              </div>

              {/* Visual Map of the entire structure as the first block */}
              {renderVisualStructureMap(rootNode)}

              {/* Hierarchy representation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Root leader card */}
                <div className="col-span-1 md:col-span-2 glass-card rounded-xl p-3.5 border border-emerald-500/30 bg-emerald-500/5 print:bg-gray-50 print:border-gray-300 print-avoid-break">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-accent-neon flex items-center justify-center font-bold">
                        1
                      </div>
                      <div>
                        <div className="font-bold text-sm text-white print:text-black">
                          {rootNode.name} <span className="text-xs text-accent-neon print:text-emerald-700">(Лідер структури)</span>
                        </div>
                        <div className="text-xs text-text-secondary print:text-gray-600">
                          Рівень {rootNode.level} • Особисті: {formatAE(rootNode.personalMonthlyAE)} • Всього по групі: {formatAE(rootNode.totalMonthlyAE)}
                        </div>
                      </div>
                    </div>
                    <Badge level={rootNode.level} size="sm">Р{rootNode.level}</Badge>
                  </div>
                </div>

                {/* Direct lines cards */}
                {rootNode.children.map((branch, idx) => (
                  <div
                    key={branch.id}
                    className="glass-card rounded-xl p-3.5 border border-white/10 print:border-gray-200 print:bg-white space-y-2 print-avoid-break"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-cyan-400 print:text-cyan-700 font-bold">
                          #{idx + 1}
                        </span>
                        <span className="font-semibold text-xs text-white print:text-black truncate">
                          {branch.name}
                        </span>
                      </div>
                      <Badge level={branch.level} size="sm">
                        Р{branch.level}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[11px] text-text-secondary print:text-gray-600 pt-1 border-t border-white/5 print:border-gray-100">
                      <div>
                        Обіг гілки: <strong className="text-white print:text-black font-mono">{formatAE(branch.totalMonthlyAE)}</strong>
                      </div>
                      <div className="text-right">
                        Команда: <strong className="text-white print:text-black font-mono">{countTotalConsultants(branch)} чол.</strong>
                      </div>
                    </div>

                    {/* Nested downline breakdown for this branch */}
                    {branch.children && branch.children.length > 0 && (
                      <div className="pt-2 border-t border-white/5 print:border-gray-100">
                        <div className="text-[10px] font-semibold text-cyan-400 print:text-cyan-800 uppercase tracking-wider">
                          Структура гілки ({countTotalConsultants(branch)} чол.):
                        </div>
                        {renderDownlineBranch(branch)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 2: Key Metrics & Goals */}
          {includeMetrics && (
            <div className="space-y-4 pt-4 print:pt-6 print-page-break print-avoid-break">
              <div className="flex items-center gap-2 text-sm font-bold text-accent-emerald uppercase tracking-wider print:text-emerald-800 border-b border-white/10 pb-2">
                <BarChart3 className="w-4 h-4 flex-shrink-0" />
                <span>2. Показники діяльності та кваліфікаційні цілі</span>
              </div>

              {/* 4 KPIs Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print-avoid-break">
                <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-gray-50 text-center">
                  <div className="text-[11px] text-text-muted print:text-gray-500">Особистий обіг</div>
                  <div className="text-base sm:text-lg font-bold font-mono text-white print:text-black mt-0.5">
                    {formatAE(rootNode.personalMonthlyAE)}
                  </div>
                </div>

                <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-gray-50 text-center">
                  <div className="text-[11px] text-text-muted print:text-gray-500">Структурний обіг</div>
                  <div className="text-base sm:text-lg font-bold font-mono text-accent-emerald print:text-emerald-700 mt-0.5">
                    {formatAE(rootNode.totalMonthlyAE)}
                  </div>
                </div>

                <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-gray-50 text-center">
                  <div className="text-[11px] text-text-muted print:text-gray-500">Консультантів</div>
                  <div className="text-base sm:text-lg font-bold font-mono text-cyan-400 print:text-cyan-700 mt-0.5">
                    {activeConsultants} / {totalConsultants}
                  </div>
                </div>

                <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-gray-50 text-center">
                  <div className="text-[11px] text-text-muted print:text-gray-500">Чек (Брутто)</div>
                  <div className="text-base sm:text-lg font-bold font-mono text-accent-neon print:text-emerald-800 mt-0.5">
                    {formatCurrencyUAH(payout.totalGrossIncomeUAH, 0)}
                  </div>
                </div>
              </div>

              {/* Career Goal Progress Box */}
              {gapAnalysis.nextLevel && (
                <div className="glass-card rounded-xl p-3.5 border border-cyan-500/20 bg-cyan-500/5 print:bg-gray-50 print:border-gray-200 space-y-2 text-xs print-avoid-break">
                  <div className="flex items-center justify-between font-medium">
                    <span className="text-cyan-300 print:text-cyan-800">
                      Ціль: Рівень {gapAnalysis.nextLevel} ({getLevelTitle(gapAnalysis.nextLevel)})
                    </span>
                    <span className="font-mono text-white print:text-black font-bold">
                      {gapAnalysis.progressPercent}% виконання
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-800 print:bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-cyan-400 rounded-full transition-all"
                      style={{ width: `${gapAnalysis.progressPercent}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-text-muted print:text-gray-600 flex justify-between">
                    <span>Накопичено: {formatAE(rootNode.totalCumulativeAE, 0)}</span>
                    <span>Поріг: {formatAE(gapAnalysis.targetThresholdAE, 0)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SECTION 3: Detailed Financial Check & Tax Statement */}
          {includeCheck && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-300 uppercase tracking-wider print:text-amber-800 border-b border-white/10 print:border-gray-200 pb-2">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span>3. Деталізація винагороди та чистий дохід («на руки»)</span>
              </div>

              {/* Lines Breakdown Table */}
              <div className="space-y-2.5 text-xs">
                {/* 1. Personal sales */}
                <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-gray-50 flex items-center justify-between print-avoid-break">
                  <div>
                    <div className="font-semibold text-white print:text-black">
                      1. Особисті продажі ({formatAE(payout.personalAE)})
                    </div>
                    <div className="text-[11px] text-text-muted print:text-gray-600">
                      Ставка: {formatCurrencyUAH(payout.personalRateUAH, 2)}
                      {payout.rateCoefficient && payout.rateCoefficient !== 1.0
                        ? ` × коефіцієнт ${payout.rateCoefficient} (1-й рік)`
                        : ' (100% повна комісія)'}
                    </div>
                  </div>
                  <div className="font-mono font-bold text-accent-neon print:text-emerald-700 text-sm">
                    {formatCurrencyUAH(payout.personalCommissionUAH, 2)}
                  </div>
                </div>

                {/* 2. Direct lines */}
                {payout.lineBreakdowns.length > 0 && (
                  <div className="glass-card rounded-xl p-3 border border-white/5 print:border-gray-200 print:bg-white space-y-2 print-avoid-break">
                    <div className="font-semibold text-white print:text-black flex items-center justify-between">
                      <span>2. Комісія з прямих ліній</span>
                      <span className="font-mono text-cyan-400 print:text-cyan-700">
                        {formatCurrencyUAH(payout.totalInterLevelCommissionUAH, 2)}
                      </span>
                    </div>

                    <div className="divide-y divide-white/5 print:divide-gray-100 pt-1">
                      {payout.lineBreakdowns.map((line) => (
                        <div key={line.lineLeaderId} className="py-1.5 flex items-center justify-between text-[11px]">
                          <div>
                            <span className="font-medium text-white print:text-black">{line.lineLeaderName}</span>{' '}
                            <span className="text-text-muted print:text-gray-500">(Р{line.lineLeaderLevel}, обіг {formatAE(line.lineMonthlyTurnoverAE)})</span>
                          </div>
                          <div className="font-mono font-semibold text-cyan-300 print:text-cyan-800">
                            +{formatCurrencyUAH(line.lineCommissionUAH, 2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. Director/Coowner bonus if applicable */}
                {(isDirector || isCoOwner) && (
                  <div className="glass-card rounded-xl p-3 border border-amber-500/20 bg-amber-500/5 print:bg-gray-50 print:border-gray-200 flex items-center justify-between print-avoid-break">
                    <div>
                      <div className="font-semibold text-amber-200 print:text-amber-800">
                        3. Кваліфікаційні бонуси (Директорський / Співвласник)
                      </div>
                      <div className="text-[11px] text-text-muted print:text-gray-600">
                        {isCoOwner ? (
                          <>
                            Директорський (≤Р5): {formatAE(payout.directorBonusQualifiedTurnoverAE)} • Співвласницький (≤Р7): {formatAE(payout.coOwnerBonusQualifiedTurnoverAE)}
                          </>
                        ) : (
                          <>
                            Кваліфікований обіг (≤Р5): {formatAE(payout.directorBonusQualifiedTurnoverAE)}
                          </>
                        )}
                        {' • Виплачується на 100% у повному розмірі'}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-amber-400 print:text-amber-700 text-sm">
                      +{formatCurrencyUAH(payout.directorBonusUAH + payout.coOwnerBonusUAH, 2)}
                    </div>
                  </div>
                )}

                {/* Total Gross and Net Take-home Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 print-avoid-break">
                  {/* Gross Summary */}
                  <div className="bg-slate-900 print:bg-gray-100 rounded-xl p-3.5 border border-white/10 print:border-gray-300 space-y-1">
                    <div className="text-[11px] text-text-muted print:text-gray-600 uppercase font-semibold">
                      Всього нараховано (Брутто):
                    </div>
                    <div className="text-xl font-bold font-mono text-white print:text-black">
                      {formatCurrencyUAH(payout.totalGrossIncomeUAH, 2)}
                    </div>
                    <div className="text-[11px] text-rose-400 print:text-rose-700">
                      Податки (ФОП 3-тя гр.): -{formatCurrencyUAH(payout.totalTaxesUAH, 2)}
                    </div>
                  </div>

                  {/* Net Summary */}
                  <div className="bg-gradient-to-br from-emerald-950/60 to-slate-900 print:bg-emerald-50 rounded-xl p-3.5 border border-emerald-500/30 print:border-emerald-300 space-y-1">
                    <div className="text-[11px] text-emerald-300 print:text-emerald-800 uppercase font-semibold flex items-center justify-between">
                      <span>Чистий дохід («на руки»):</span>
                      <span className="font-mono">Збереження {formatPercent(1 - effectiveTaxRate, 1)}</span>
                    </div>
                    <div className="text-2xl font-bold font-mono text-accent-neon print:text-emerald-800">
                      {formatCurrencyUAH(payout.estimatedNetIncomeUAH, 2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Official Document Footer */}
          <div className="pt-6 border-t border-white/15 print:border-gray-200 text-[10px] text-text-muted print:text-gray-500 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>Starlife Life Insurance Broker • Система розрахунків винагороди консультантів</span>
            <span>Конфіденційно • Для внутрішнього планування консультанта</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};
